import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import { useAuth } from './auth'
import type { Church, ChurchGiver, ChurchInfo, ChurchMember, ChurchPage } from '@/types'
import type { ChurchPlace } from '@/lib/churchSearch'
import type { ChurchSkinChoice } from '@/features/church/skins'

// Your church, what you've given it, and how it stacks up locally.
//
// This one is ONLINE-only on purpose: a church is a shared, pooled thing, so
// there's nothing meaningful to keep on a single device. Guests see a sign-in
// prompt instead (same as the worldwide leaderboard).

/** What the board can be scoped to: a radius in miles, or every church there is. */
export const RADIUS_CHOICES = [10, 20, 30, 50, 'all'] as const
export type RadiusChoice = (typeof RADIUS_CHOICES)[number]
export const DEFAULT_RADIUS: RadiusChoice = 20
const RADIUS_KEY = 'va.churchRadius'

/**
 * How far back the board counts: today, this week, or every point ever banked.
 *
 * The two windows are UTC, and that is inherited from the rivalry (0075)
 * rather than chosen here — see the header of `0080_church_leaderboard_window.sql`.
 * "This week" IS the rivalry's week, derived from the same epoch on both sides
 * of the wire, so the number on the board and the number on the rivalry card
 * are the same number for the same church and roll over together.
 *
 * All time stays the default: it is the only one of the three that reads a
 * counter rather than summing three ledgers, and it is what the board has
 * always shown.
 */
export const TIMEFRAME_CHOICES = ['day', 'week', 'all'] as const
export type TimeframeChoice = (typeof TIMEFRAME_CHOICES)[number]
export const DEFAULT_TIMEFRAME: TimeframeChoice = 'all'
const TIMEFRAME_KEY = 'va.churchWindow'

const isRadiusChoice = (v: unknown): v is RadiusChoice =>
  (RADIUS_CHOICES as readonly unknown[]).includes(v)

const isTimeframe = (v: unknown): v is TimeframeChoice =>
  (TIMEFRAME_CHOICES as readonly unknown[]).includes(v)

function readRadius(): RadiusChoice {
  try {
    const raw = localStorage.getItem(RADIUS_KEY)
    if (raw === 'all') return 'all'
    const n = Number(raw)
    return isRadiusChoice(n) ? n : DEFAULT_RADIUS
  } catch {
    return DEFAULT_RADIUS
  }
}

function readTimeframe(): TimeframeChoice {
  try {
    const raw = localStorage.getItem(TIMEFRAME_KEY)
    return isTimeframe(raw) ? raw : DEFAULT_TIMEFRAME
  } catch {
    return DEFAULT_TIMEFRAME
  }
}

function isOnline(): boolean {
  const a = useAuth.getState()
  return !!supabase && a.mode === 'online' && a.isAuthed
}

// The RPCs speak snake_case; the app speaks camelCase.
// The device's copy of the player's own church (get_my_church's answer), per
// account, for the offline tab. Read only when the server can't be reached.
const KEPT_CHURCH = 'va.myChurch'
function keepChurch(uid: string, payload: unknown) {
  try { localStorage.setItem(KEPT_CHURCH, JSON.stringify({ uid, payload })) } catch { /* storage blocked */ }
}
function readKeptChurch(uid: string): any {
  try {
    const k = JSON.parse(localStorage.getItem(KEPT_CHURCH) ?? 'null')
    return k && k.uid === uid ? k.payload : null
  } catch { return null }
}

function toChurch(raw: any): Church | null {
  if (!raw) return null
  return {
    id: raw.id,
    name: raw.name,
    address: raw.address ?? null,
    city: raw.city ?? null,
    region: raw.region ?? null,
    lat: Number(raw.lat),
    lng: Number(raw.lng),
    xp: Number(raw.xp ?? 0),
    // Only `church_leaderboard` (0080) sends this, and a server that predates
    // it never does — so an older backend leaves it undefined and the board
    // falls back to lifetime XP, which is exactly how it used to read.
    points: raw.points != null ? Number(raw.points) : undefined,
    level: Number(raw.level ?? 1),
    members: Number(raw.members ?? 0),
    miles: raw.miles != null ? Number(raw.miles) : undefined,
    rank: raw.rank != null ? Number(raw.rank) : undefined,
    isMine: raw.is_mine ?? undefined,
    // Set on every church by `church_json` (0051), so board rows, the page and
    // your own church tab all draw the same building.
    skin: raw.skin ?? null,
  }
}

function toGiver(raw: any): ChurchGiver {
  return {
    username: raw.username,
    avatarEmoji: raw.avatar_emoji ?? '📖',
    avatarCharacter: raw.avatar_character ?? null,
    points: Number(raw.points ?? 0),
    isMe: !!raw.is_me,
  }
}

function toMember(raw: any): ChurchMember {
  return {
    username: raw.username,
    avatarEmoji: raw.avatar_emoji ?? '📖',
    avatarCharacter: raw.avatar_character ?? null,
    // Absent on a server that predates 0072 — a figure with no companion.
    pet: raw.pet ?? null,
    isMe: !!raw.is_me,
  }
}

// The public page's roster: the same crowd, with nobody named.
//
// `public_church_page` (0074) returns no username at all, so the figures need
// something else to seed their walk from or they all share one schedule and
// move as a single body. Their position in a join-ordered list is stable across
// loads and says nothing about anybody, which is exactly what's wanted.
function toAnonMember(raw: any, i: number): ChurchMember {
  return {
    username: '',
    seed: `anon-${i}`,
    avatarEmoji: raw.avatar_emoji ?? '📖',
    avatarCharacter: raw.avatar_character ?? null,
    pet: raw.pet ?? null,
    isMe: false,
  }
}

function toInfo(raw: any): ChurchInfo | null {
  if (!raw) return null
  const info: ChurchInfo = {
    tagline: raw.tagline ?? null,
    thisWeek: typeof raw.thisWeek === 'string' && raw.thisWeek.trim() ? raw.thisWeek : null,
    about: raw.about ?? null,
    serviceTimes: raw.serviceTimes ?? null,
    website: raw.website ?? null,
    contact: raw.contact ?? null,
  }
  // A published-but-blank profile is nothing to show; treat it as absent so the
  // page offers the "Add info" pill instead of an empty panel.
  return Object.values(info).some((v) => !!v) ? info : null
}

export interface ContributeResult {
  ok: boolean
  given: number
  leveledUp: boolean
  reason?: string
}

/** Who's asking for a church's page to be filled in. */
export type InfoRequestRole = 'leadership' | 'member'

/** Note length by role — mirrored exactly in `submit_church_info_request` (0050). */
export const INFO_NOTE_MAX: Record<InfoRequestRole, number> = {
  leadership: 500,
  // Someone who just attends is passing on a fact, not writing the page.
  member: 180,
}
export const INFO_NOTE_MIN = 10

export interface InfoRequestInput {
  churchId: string
  role: InfoRequestRole
  note: string
  name?: string
  email?: string
  /**
   * Leadership ticked "tell us about promoting our church" (0078). An ASK and
   * nothing more — it grants no slot, names no price, and only
   * `admin_set_church_promotion` (0077) can actually start one. Dropped on the
   * member path by the server, like `skin`.
   */
  wantsPromotion?: boolean
  /**
   * The look the church is asking for — a `ChurchSkinChoice`, or undefined for
   * "no preference". Leadership only: `submit_church_info_request` (0051) drops
   * it on the member path rather than trusting the form, because someone who
   * merely attends doesn't get to redecorate the building.
   */
  skin?: ChurchSkinChoice
}

interface ChurchState {
  church: Church | null
  /** Points you can still give: lifetime XP minus everything already given. */
  available: number
  myGiven: number
  givers: ChurchGiver[]
  board: Church[]
  boardMe: Church | null
  boardTotal: number
  radiusMiles: RadiusChoice
  timeframe: TimeframeChoice
  loaded: boolean
  loading: boolean
  boardLoading: boolean
  error: string | null

  /** The church page opened from a board row, or null when the sheet is closed. */
  page: ChurchPage | null
  /** True while the extra detail (info, roster) is still in flight. */
  pageLoading: boolean

  /**
   * Your OWN congregation, for the yard on the church tab.
   *
   * The hero used to stand you alone on the grass under a caption reading
   * "Your congregation · 3 players", which reads as a bug — because it is one,
   * in the only sense that matters: the screen said three and drew one. Your
   * church is a place you share with a handful of named people, not a faction
   * hall shared with thousands, so there is no sampling problem to dodge here.
   *
   * Same roster the page uses, ordered by join date and carrying no per-person
   * number — a crowd, not a ladder. `is_me` comes back on your own row, which
   * is what stops you being drawn twice and lets CrowdLife read your live pet
   * from the auth store rather than the row.
   */
  congregation: ChurchMember[]
  /**
   * Your OWN church's published info (tagline, "This week", service times…),
   * from the same `get_church_page` read `loadCongregation` already makes —
   * kept rather than thrown away so the profile's church card can quote it
   * without a second round trip. Null until loaded, or when nothing is published.
   */
  myInfo: ChurchInfo | null

  load: () => Promise<void>
  loadBoard: () => Promise<void>
  setRadius: (choice: RadiusChoice) => void
  setTimeframe: (choice: TimeframeChoice) => void
  join: (place: ChurchPlace) => Promise<Church | null>
  /** False when the server didn't take it: the player is still in their church. */
  leave: () => Promise<boolean>
  contribute: (points: number) => Promise<ContributeResult>
  openChurch: (church: Church) => Promise<void>
  closeChurch: () => void
  loadCongregation: () => Promise<void>
  requestInfo: (input: InfoRequestInput) => Promise<{ ok: boolean; reason?: string }>
  /**
   * Publish an edit to a church page you are verified leadership of (0079).
   * Unlike `requestInfo` this writes straight through — that's the whole point
   * of a claim — but only for the five text fields, and the server re-checks
   * the claim rather than trusting `canEdit`.
   */
  updateChurchPage: (input: ChurchPageEdit) => Promise<{ ok: boolean; reason?: string }>
}

/** What a claimed church may say about itself. Never the skin. */
export interface ChurchPageEdit {
  churchId: string
  tagline: string
  /** '' clears the line; the server caps it at 140 characters (0120). */
  thisWeek: string
  about: string
  serviceTimes: string
  website: string
  contact: string
}

export const useChurch = create<ChurchState>((set, get) => ({
  church: null,
  available: 0,
  myGiven: 0,
  givers: [],
  board: [],
  boardMe: null,
  boardTotal: 0,
  radiusMiles: readRadius(),
  timeframe: readTimeframe(),
  loaded: false,
  loading: false,
  boardLoading: false,
  error: null,
  page: null,
  pageLoading: false,
  congregation: [],
  myInfo: null,

  async load() {
    if (!isOnline()) {
      set({ loaded: true, loading: false, church: null })
      return
    }
    set({ loading: true, error: null })
    const { data, error } = await supabase!.rpc('get_my_church', { p_givers_limit: 10 })
    const uid = useAuth.getState().profile?.id ?? null
    // Offline (or the server didn't answer), the tab used to fall through to
    // the PICKER — telling a member they had no church, on a screen that can't
    // search without a network. So the last answer this device got is kept and
    // shown instead; with nothing kept, `error` makes the screen say it can't
    // reach the server rather than offer a picker.
    let payload: any = data
    if (error) {
      const kept = uid ? readKeptChurch(uid) : null
      if (!kept) {
        set({ loading: false, loaded: true, error: error.message })
        return
      }
      payload = kept
      set({ error: error.message })
    } else if (uid) {
      keepChurch(uid, payload)
    }
    set({
      church: toChurch(payload?.church),
      available: Number(payload?.available ?? 0),
      myGiven: Number(payload?.my_given ?? 0),
      givers: ((payload?.givers ?? []) as any[]).map(toGiver),
      loading: false,
      loaded: true,
    })
    if (get().church && !error) void get().loadBoard()
  },

  async loadBoard() {
    if (!isOnline() || !get().church) return
    set({ boardLoading: true })
    const radius = get().radiusMiles
    const worldwide = radius === 'all'
    const { data, error } = await supabase!.rpc('church_leaderboard', {
      // A null radius is the server's "no distance limit" signal.
      p_radius_miles: worldwide ? null : radius,
      // The worldwide ladder is worth showing deeper than the local one.
      p_limit: worldwide ? 50 : 25,
      // 0080. A server that predates it resolves the two-argument overload
      // instead and answers all-time, which is what the board used to show —
      // no error, and the chips still change the radius.
      p_window: get().timeframe,
    })
    if (error) {
      set({ boardLoading: false, error: error.message })
      return
    }
    const payload = data as any
    set({
      board: ((payload?.rows ?? []) as any[]).map(toChurch).filter(Boolean) as Church[],
      boardMe: toChurch(payload?.me),
      boardTotal: Number(payload?.total ?? 0),
      boardLoading: false,
    })
  },

  setRadius(choice) {
    if (!isRadiusChoice(choice)) return
    try {
      localStorage.setItem(RADIUS_KEY, String(choice))
    } catch {
      /* private mode — the choice just won't stick */
    }
    set({ radiusMiles: choice })
    void get().loadBoard()
  },

  setTimeframe(choice) {
    if (!isTimeframe(choice)) return
    try {
      localStorage.setItem(TIMEFRAME_KEY, choice)
    } catch {
      /* private mode — the choice just won't stick */
    }
    set({ timeframe: choice })
    void get().loadBoard()
  },

  async join(place) {
    if (!isOnline()) return null
    set({ loading: true, error: null })
    const { data, error } = await supabase!.rpc('join_church', {
      // A church we already know is joined by id — its stored key may be an OSM
      // id we can't reconstruct, and re-deriving one would fork a second row.
      p_church_id: place.churchId ?? null,
      p_place_key: place.placeKey || null,
      p_name: place.name,
      p_lat: place.lat,
      p_lng: place.lng,
      p_address: place.address ?? null,
      p_city: place.city ?? null,
      p_region: place.region ?? null,
    })
    if (error) {
      set({ loading: false, error: error.message })
      return null
    }
    const church = toChurch(data)
    set({ church, loading: false, givers: [], myGiven: 0, board: [], boardMe: null, myInfo: null })
    // Pull the real numbers (available budget, who else is here) now that we're in.
    void get().load()
    return church
  },

  async leave() {
    if (!isOnline()) return false
    // Checked: clearing the church on screen after a refused leave told the
    // player they had left while the server still counted them as a member.
    const { error } = await supabase!.rpc('leave_church')
    if (error) { void get().load(); return false }
    set({ church: null, givers: [], myGiven: 0, board: [], boardMe: null, boardTotal: 0, myInfo: null })
    void get().load()
    return true
  },

  async contribute(points) {
    if (!isOnline()) return { ok: false, given: 0, leveledUp: false, reason: 'offline' }
    const want = Math.floor(points)
    if (!(want > 0)) return { ok: false, given: 0, leveledUp: false, reason: 'nothing_to_give' }

    const { data, error } = await supabase!.rpc('contribute_to_church', { p_points: want })
    if (error) {
      set({ error: error.message })
      return { ok: false, given: 0, leveledUp: false, reason: error.message }
    }
    const payload = data as any
    if (!payload?.ok) {
      if (payload?.available != null) set({ available: Number(payload.available) })
      return { ok: false, given: 0, leveledUp: false, reason: payload?.reason ?? 'failed' }
    }

    const church = toChurch(payload.church)
    const given = Number(payload.given ?? 0)
    set((s) => ({
      church,
      available: Number(payload.available ?? 0),
      myGiven: s.myGiven + given,
    }))
    // The local board's ranks just moved, and so did our row in "top givers".
    void get().load()
    return { ok: true, given, leveledUp: !!payload.leveled_up }
  },

  // --- The church page behind a leaderboard row -----------------------------
  // Seeded from the row that was tapped so the sheet is drawn the instant it
  // opens: rank, level, XP and members are all already in hand. Only the parts
  // we don't have yet — the published info and the congregation to stand
  // outside — wait on the network.
  async openChurch(church) {
    set({
      page: { church, info: null, members: [], memberTotal: church.members, myRequestPending: false, canEdit: false },
      pageLoading: true,
    })
    if (!isOnline()) {
      set({ pageLoading: false })
      return
    }
    const { data, error } = await supabase!.rpc('get_church_page', {
      p_church_id: church.id,
      // The server's cap. The scene only draws a dozen or so, but the page also
      // names the congregation, and a name list wants to be as complete as the
      // RPC will give us.
      p_members_limit: 24,
    })
    // Tapping a second row before the first landed: whatever came back belongs
    // to a sheet that isn't on screen any more, so drop it.
    if (get().page?.church.id !== church.id) return
    if (error || !(data as any)?.ok) {
      set({ pageLoading: false })
      return
    }
    const payload = data as any
    set({
      page: {
        // Keep the row's rank: the page RPC doesn't compute one (it would mean
        // ranking the whole board again), and the rank the player just looked at
        // is the right one to keep showing.
        church: { ...(toChurch(payload.church) ?? church), rank: church.rank },
        info: toInfo(payload.info),
        members: ((payload.members ?? []) as any[]).map(toMember),
        memberTotal: Number(payload.member_total ?? church.members),
        myRequestPending: !!payload.my_request_pending,
        // Absent on a server that predates 0079 — nobody can edit, which is
        // exactly how this screen behaved before claims existed.
        canEdit: !!payload.can_edit,
      },
      pageLoading: false,
    })
  },

  closeChurch() {
    set({ page: null, pageLoading: false })
  },

  // --- Your own congregation, for the yard on the church tab ----------------
  // Reuses get_church_page rather than adding an RPC: it already returns the
  // roster for ANY church, your own included, and a second function returning
  // the same rows is two places to keep the "no per-person number" rule.
  //
  // Offline and for a guest this leaves the list empty and the scene falls back
  // to drawing you alone, which is the honest picture when there is no server
  // to ask — the church store's inherited online-only break, not a new one.
  async loadCongregation() {
    const mine = get().church
    if (!mine || !isOnline()) {
      set({ congregation: [], myInfo: null })
      return
    }
    const { data, error } = await supabase!.rpc('get_church_page', {
      p_church_id: mine.id,
      p_members_limit: 24,
    })
    // Left or switched church while this was in flight: the rows that came back
    // belong to a yard that isn't on screen any more.
    if (get().church?.id !== mine.id) return
    if (error || !(data as any)?.ok) return
    set({
      congregation: ((data as any).members ?? []).map(toMember),
      myInfo: toInfo((data as any).info),
    })
  },

  async updateChurchPage({ churchId, tagline, thisWeek, about, serviceTimes, website, contact }) {
    if (!isOnline()) return { ok: false, reason: 'offline' }
    const { data, error } = await supabase!.rpc('update_my_church_profile', {
      p_church_id: churchId,
      p_tagline: tagline.trim() || null,
      p_about: about.trim() || null,
      p_service_times: serviceTimes.trim() || null,
      p_website: website.trim() || null,
      p_contact: contact.trim() || null,
      // Always sent, '' to clear: the server reads NULL as "not sent" and
      // keeps the old line, which is how an older app avoids wiping it.
      p_this_week: thisWeek.trim(),
    })
    if (error) return { ok: false, reason: error.message }
    const payload = data as any
    if (!payload?.ok) return { ok: false, reason: payload?.reason ?? 'failed' }
    // Show what was just published rather than re-fetching: the server took
    // exactly these five strings, so the page can say so immediately. Cleared
    // to null when every field is empty, the same rule `toInfo` applies to a
    // published-but-blank profile — otherwise the page draws an empty panel.
    const next: ChurchInfo = {
      tagline: tagline.trim() || null,
      thisWeek: thisWeek.trim().replace(/[\u0000-\u001f\u007f]+/g, ' ').slice(0, 140) || null,
      about: about.trim() || null,
      serviceTimes: serviceTimes.trim() || null,
      website: website.trim() || null,
      contact: contact.trim() || null,
    }
    const anything = Object.values(next).some((v) => !!v)
    set((s) =>
      s.page?.church.id === churchId
        ? { page: { ...s.page, info: anything ? next : null } }
        : {},
    )
    return { ok: true }
  },

  async requestInfo({ churchId, role, note, name, email, skin, wantsPromotion }) {
    if (!isOnline()) return { ok: false, reason: 'offline' }
    const { data, error } = await supabase!.rpc('submit_church_info_request', {
      p_church_id: churchId,
      p_role: role,
      p_note: note.slice(0, INFO_NOTE_MAX[role]),
      p_name: name?.trim() || null,
      p_email: email?.trim() || null,
      p_skin: skin ?? null,
      p_wants_promotion: !!wantsPromotion,
    })
    if (error) return { ok: false, reason: error.message }
    const payload = data as any
    if (!payload?.ok) return { ok: false, reason: payload?.reason ?? 'failed' }
    // The pill stops inviting a second note the moment the first one lands.
    set((s) =>
      s.page?.church.id === churchId ? { page: { ...s.page, myRequestPending: true } } : {},
    )
    return { ok: true }
  },
}))

// ---------------------------------------------------------------------------
// One church, by id — the page behind /church/:id
// ---------------------------------------------------------------------------
// A standalone function rather than another slot on the store, because the
// store's `page` belongs to the sheet: it is opened from a board row it was
// seeded by and closed by a scrim tap, and a screen borrowing that slot would
// fight the sheet's lifecycle over who gets to null it.
//
// Two RPCs, one URL. A signed-in visitor gets `get_church_page` and the full
// page they already know, names and all; anyone else gets `public_church_page`,
// which draws the same congregation without naming it (see 0074 for why). The
// screen doesn't branch — it asks, and renders whatever detail came back.
export async function fetchChurchPage(churchId: string): Promise<ChurchPage | null> {
  if (!supabase) return null

  if (isOnline()) {
    const { data, error } = await supabase.rpc('get_church_page', {
      p_church_id: churchId,
      p_members_limit: 24,
    })
    if (!error && (data as any)?.ok) {
      const payload = data as any
      const church = toChurch(payload.church)
      if (church) {
        return {
          church,
          info: toInfo(payload.info),
          members: ((payload.members ?? []) as any[]).map(toMember),
          memberTotal: Number(payload.member_total ?? 0),
          myRequestPending: !!payload.my_request_pending,
          canEdit: !!payload.can_edit,
        }
      }
    }
    // Fall through on failure rather than showing nothing: the public read is
    // anon-callable, so a signed-in visitor whose session went stale still gets
    // the church rather than an error page.
  }

  const { data, error } = await supabase.rpc('public_church_page', {
    p_church_id: churchId,
    p_members_limit: 24,
  })
  if (error || !(data as any)?.ok) return null
  const payload = data as any
  const church = toChurch(payload.church)
  if (!church) return null
  return {
    church,
    info: toInfo(payload.info),
    members: ((payload.members ?? []) as any[]).map(toAnonMember),
    memberTotal: Number(payload.member_total ?? 0),
    myRequestPending: false,
    // `public_church_page` (0074) is the signed-out read and knows nothing
    // about who is looking, so the editor never appears on it.
    canEdit: false,
  }
}

// Back online with a tab showing a kept (or no) church: read the real one.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    const c = useChurch.getState()
    if (c.loaded && c.error) void c.load()
  })
}
